import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Bleed, Checkbox, InfoSheet, Radio, RadioGroup, Toggle, ToggleGroup, H6, Subheading, Select } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'

import styles from './SearchResultsColumnPicker.styles'
import transl from './SearchResultsColumnPicker.transl'
import useColumns from './useColumns'

const configs = {
  GLOBAL: 'global',
  JIAPU: 'jiapu',
  JOKBO: 'jokbo',
  KEIZU: 'keizu',
}
const modes = {
  GENERAL_PUBLIC: 'GENERAL_PUBLIC',
  INTERNAL_STAFF: 'INTERNAL_STAFF',
}

export default function SearchResultsColumnPicker({
  columnConfig = configs.GLOBAL,
  infoSheet,
  mode = modes.GENERAL_PUBLIC,
  searchCriteria,
  selectedColumnNames,
  setMode,
  setSelectedColumnNames,
  showMiniMap,
  showModePicker,
  updateGlobalMapToggle,
  updateSearchCriteria,
}) {
  function handleCheckboxChange({ target: { checked, name } }) {
    let newSelectedColumns = [...selectedColumnNames]

    if (checked) {
      newSelectedColumns = [...selectedColumnNames, name]
    } else {
      newSelectedColumns.splice(selectedColumnNames.indexOf(name), 1)
    }

    setSelectedColumnNames(newSelectedColumns)
  }

  function handleRadioChange({ target: { value } }) {
    setMode(value)
  }

  let columnsToShow = useColumns(columnConfig)

  if (mode === modes.GENERAL_PUBLIC) {
    columnsToShow = columnsToShow.filter((column) => !column.internalOnly)
  }

  columnsToShow = columnsToShow.filter((column) => column.id !== 'actions')

  const romanizationOptions = {
    [configs.JIAPU]: [
      {
        label: transl.romanizationOptionsMandarin(),
        options: [
          { label: transl.romanizationOptionsPinYin(), value: 'en' },
          { label: transl.romanizationOptionsWadegiles(), value: 'en-wadegile' },
        ],
      },
      {
        label: transl.romanizationOptionsCantonese(),
        options: [
          { label: transl.romanizationOptionsYale(), value: 'en-x-yale' },
          { label: transl.romanizationOptionsJyutping(), value: 'en-x-tongyong' },
        ],
      },
    ],
    [configs.JOKBO]: [{ label: transl.romanizationOptionsKorean(), value: 'ko_Latn' }],
  }

  const hasRomanizationOptions = [configs.JIAPU, configs.JOKBO]
  const showRomanizationSelect = i18n.language === 'en' && hasRomanizationOptions.includes(columnConfig)

  const [selectedRomanization, setSelectedRomanization] = useState(
    searchCriteria?.romanizeLocale ? searchCriteria?.romanizeLocale : ''
  )

  const onChangeRomanizationSelect = (event) => {
    setSelectedRomanization(event.target.value)
    updateSearchCriteria({ romanizeLocale: event.target.value })
  }

  return (
    <InfoSheet title={transl.options()} {...infoSheet}>
      {showModePicker && (
        <Bleed start end>
          <section css={styles.radioSection}>
            <RadioGroup>
              <H6>
                {transl.viewMode()}
                <Subheading>{transl.internalStaffOnlyMessage()}</Subheading>
              </H6>
              <div css={styles.rowWithLock}>
                <Radio
                  checked={mode === modes.INTERNAL_STAFF}
                  label={transl.radioInternalStaff()}
                  name="public-or-internal"
                  onChange={handleRadioChange}
                  value={modes.INTERNAL_STAFF}
                />
              </div>

              <Radio
                checked={mode === modes.GENERAL_PUBLIC}
                label={transl.radioGeneralPublic()}
                name="public-or-internal"
                onChange={handleRadioChange}
                value={modes.GENERAL_PUBLIC}
              />
            </RadioGroup>
          </section>
        </Bleed>
      )}

      {columnConfig === 'global' && (
        <>
          <H6>{`${transl.searchByMap()}`}</H6>
          <ToggleGroup>
            <Toggle
              label={transl.showMap()}
              checked={showMiniMap}
              onChange={updateGlobalMapToggle}
              allowLabelWrapping
            />
          </ToggleGroup>
        </>
      )}

      <H6>{`${transl.contentToShow()}:`}</H6>
      {columnsToShow.map((column) => {
        const selected = selectedColumnNames.includes(column.id)
        const disabled = selected && selectedColumnNames.length === 1

        return column.internalOnly ? (
          <InternalCheckboxRow
            checked={selected}
            disabled={disabled}
            id={column.id}
            key={column.id}
            label={column.label()}
            onChange={handleCheckboxChange}
          />
        ) : (
          <Checkbox
            checked={selected}
            disabled={disabled}
            key={column.id}
            label={column.label()}
            name={column.id}
            onChange={handleCheckboxChange}
          />
        )
      })}
      {showRomanizationSelect && (
        <Select
          label={transl.romanizationSelectLabel()}
          placeholder={transl.romanizationSelectPlaceholder()}
          options={romanizationOptions[columnConfig]}
          value={selectedRomanization}
          onChange={onChangeRomanizationSelect}
        />
      )}
    </InfoSheet>
  )
}

SearchResultsColumnPicker.useColumns = useColumns
SearchResultsColumnPicker.configs = configs
SearchResultsColumnPicker.modes = modes

// NOTE: I did not include prop types for infoSheet since that is already documented in InfoSheet
SearchResultsColumnPicker.propTypes = {
  columnConfig: PropTypes.string,
  mode: PropTypes.string,
  searchCriteria: PropTypes.shape({}),
  selectedColumnNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  setMode: PropTypes.func.isRequired,
  setSelectedColumnNames: PropTypes.func.isRequired,
  showModePicker: PropTypes.bool,
  updateGlobalMapToggle: PropTypes.func,
  updateSearchCriteria: PropTypes.func,
}

function InternalCheckboxRow({ checked, id, label, onChange }) {
  return (
    <Bleed start end>
      <section css={styles.internalCheckbox}>
        <div css={styles.rowWithLock}>
          <Checkbox checked={checked} label={label} name={id} onChange={onChange} />
        </div>
      </section>
    </Bleed>
  )
}

InternalCheckboxRow.propTypes = {
  checked: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}
